import { createContext, useContext, useState, useEffect, useMemo } from "react";

export const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    const savedLang = localStorage.getItem("lang");
    return savedLang || "fr";
  });

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  const translations = useMemo(() => {
    const dict = {
      fr: {
        appName: "Ranch de Ngaoundaba",
        openMenu: "Ouvrir le menu",
        closeMenu: "Fermer le menu",
        disconnect: "Déconnexion",
        language: "Langue",
        settings: "Paramètres",
        createUser: "Créer un utilisateur",
        darkMode: "Mode sombre",
        lightMode: "Mode clair",
        loading: "Chargement...",
        guest: "Invité",
        user: "Utilisateur",
        // Navigation sidebar
        navDashboard: "Dashboard",
        navAnimals: "Animaux",
        navHerds: "Troupeaux",
        navProphylaxis: "Prophylaxie Et Traitement Curatif",
        navPharmacy: "Pharmacie",
        navEvents: "Evenements",
        navReports: "Rapports",
        navAlerts: "Alertes",
        navUsers: "Utilisateurs",
        navShepherds: "Bergers",
        navDisconnect: "Déconnexion",
        brandSubtitle: "Gestion du cheptel",
        // Messages d'erreur
        errorLoadingDashboard: "Impossible de charger les données du tableau de bord.",
        sessionExpired: "Session expirée. Redirection...",
        accessDenied: "Accès refusé. Vous n'avez pas les permissions nécessaires.",
        endpointNotFound: "L'endpoint API n'a pas été trouvé. Vérifiez la configuration.",
        serverError: "Erreur serveur. Veuillez réessayer plus tard.",
        connectionError: "Impossible de se connecter au serveur. Vérifiez votre connexion et l'URL de l'API.",
        configError: "Erreur de configuration",
        // Dashboard
        welcomeDashboard: "BIENVENUE SUR LE DASHBOARD",
        error: "ERREUR",
        loadingDashboard: "Chargement du tableau de bord...",
        statistics: "Statistiques du cheptel",
        indicators: "Indicateurs (%)",
        monthlyEvolution: "Evolution Mensuelle",
        sexDistribution: "Répartition Par Sexe",
        categoryDistribution: "Répartition Par Catégorie",
        animals: "animaux",
        numberOfAnimals: "Nombre d'animaux",
        // Labels communs
        total: "Total",
        males: "Mâles",
        females: "Femelles",
        // Labels StatCard Dashboard
        initialStock: "EFFECTIF INITIAL",
        births: "NAISSANCES",
        mortality: "MORTALITE",
        purchases: "ACHATS",
        sales: "VENTES",
        zakat: "ZAKAT",
        finalStock: "EFFECTIF FINAL",
        estimatedValue: "VALEUR ESTIMEE",
        // Labels StatCard FicheAnimal
        estimatedValueFull: "VALEUR ESTIMÉE",
        prophylaxes: "PROPHYLAXIES",
        reproductions: "REPRODUCTIONS",
        events: "ÉVÉNEMENTS",
        age: "ÂGE",
        status: "STATUT",
        notProvided: "Non renseigné",
        active: "Actif",
        // Labels StatCard DashboardPharmacie
        activeProducts: "Produits actifs",
        availableStocks: "Stocks disponibles",
        lowStock: "Stock faible",
        expiringSoon: "Expire bientôt",
        totalInvoices: "Total factures",
        totalAmount: "Montant total",
        overview: "Vue d'ensemble",
        quickActions: "Actions rapides",
        see: "Voir",
        category: "Catégorie",
        pharmacyDashboard: "DASHBOARD PHARMACIE",
        addProduct: "Ajouter un produit",
        addInvoice: "Ajouter une facture",
        viewUsages: "Voir les utilisations",
        // Pages de liste
        animalList: "LISTE DES ANIMAUX",
        addAnimal: "Ajouter un animal",
        filters: "Filtres",
        filterBySex: "Par sexe",
        filterByStatus: "Par statut",
        filterByCategory: "Par categorie",
        filterByHerd: "Par troupeau",
        male: "Mâle",
        female: "Femelle",
        sold: "Vendu",
        dead: "Mort",
        death: "Décès",
        cannotEditSoldOrDead: "Les animaux vendus ou morts ne peuvent pas être modifiés",
        errorLoadingAnimals: "Impossible de charger les données des animaux.",
        noAnimalsFound: "Aucun animal trouvé avec les filtres sélectionnés.",
        noAnimalsRegistered: "Aucun animal enregistré.",
        notAvailable: "N/A",
        herdList: "LISTE DES TROUPEAUX",
        addHerd: "Ajouter un troupeau",
        searchByMatricule: "Rechercher par matricule, robe, race...",
        searchByHerdName: "Rechercher par nom de troupeau...",
        filterByUser: "Par utilisateur",
        filterByShepherd: "Par berger",
        noHerdRegistered: "Aucun troupeau enregistré.",
        // Troupeaux
        herdName: "Nom Troupeau",
        responsibleUser: "Responsable (User)",
        shepherd: "Berger",
        viewMore: "Voir plus",
        loadingHerds: "Chargement des troupeaux...",
        errorLoadingHerds: "Impossible de charger les données des troupeaux.",
        // Users
        firstName: "Prénom",
        lastName: "Nom",
        groups: "Groupes",
        loadingUsers: "Chargement des utilisateurs...",
        errorLoadingUsers: "Impossible de charger les utilisateurs.",
        errorDeletingUser: "Une erreur s'est produite lors de la suppression.",
        confirmDeleteUser: "Êtes-vous sûr de vouloir supprimer cet utilisateur ?",
        userDeletedSuccess: "Utilisateur supprimé avec succès !",
        // Login
        welcome: "Bienvenue",
        heroSubtitle: "Gérez votre cheptel, vos alertes et vos rapports depuis une seule interface moderne.",
        dashboardRealtime: "Dashboard en temps réel",
        alertTracking: "Suivi des alertes",
        detailedReports: "Rapports détaillés",
        loginTitle: "Connexion",
        loginSubtitle: "Accédez à votre espace de gestion",
        loginButton: "Se connecter",
        loggingIn: "Connexion...",
        loginError: "Nom d'utilisateur ou mot de passe incorrect.",
        loginHelp: "Besoin d'aide ? Contactez votre administrateur pour réinitialiser vos accès.",
        // Rapports
        herdReport: "RAPPORT DU CHEPTEL",
        keyIndicators: "Indicateurs Clés",
        element: "Élément",
        value: "Valeur",
        totalStock: "Effectif Total",
        birthStatistics: "Statistiques Naissances",
        indicator: "Indicateur",
        numberOfCalves: "Nombre de veaux",
        numberOfHeifers: "Nombre de velles",
        numberOfCows: "Nombre de vaches",
        birthRatePerCows: "Taux de naissance / vaches (%)",
        mortalityStatistics: "Statistiques Mortalités",
        deaths: "Morts",
        mortalityRate: "Taux de mortalité (%)",
        global: "Global",
        herdDistribution: "Répartition du Cheptel",
        stockCount: "Effectif",
        herdValue: "Valeur du Cheptel",
        loadingReport: "Chargement du rapport...",
        errorLoadingReport: "Impossible de charger le rapport global.",
        // Alertes
        alertsTitle: "ALERTES : PROCHAINES INTERVENTIONS",
        viewAnimals: "Voir les animaux",
        loadingAlerts: "Chargement des alertes...",
        errorLoadingAlerts: "Erreur lors du chargement des alertes.",
        noInterventionsScheduled: "Aucune intervention n'est prévue dans les 7 prochains jours.",
        animalDetails: "Détails des animaux",
        product: "Produit",
        close: "Fermer",
        // RapportTroupeau
        herdReportTitle: "RAPPORT TROUPEAU",
        reportTitle: "RAPPORT :",
        shepherdName: "Nom du berger",
        calculatedTotal: "Total calculé",
        currentStock: "Effectif actuel",
        numberOfAnimalsByCategory: "Nombre de",
        noReportAvailable: "Aucun rapport disponible pour ce troupeau.",
        errorLoadingHerdReport: "Impossible de charger le rapport du troupeau.",
        herdNotFound: "Le troupeau demandé n'existe pas ou le rapport n'est pas disponible.",
        noPermissionReport: "Vous n'avez pas la permission d'accéder à ce rapport.",
        missingHerdId: "ID du troupeau manquant.",
        back: "Retour",
        // Historique
        historyTitle: "Historique",
        searchHistory: "Rechercher dans l'historique...",
        action: "Action",
        loadingHistory: "Chargement de l'historique...",
        errorLoadingHistory: "Erreur lors du chargement de l'historique.",
        noHistoryRecorded: "Aucun historique enregistré pour l'instant.",
        filterByAction: "Filtrer par action",
        filterByAnimal: "Filtrer par animal",
        startDate: "Date début",
        endDate: "Date fin",
        care: "Soin",
        feeding: "Alimentation",
        notAssigned: "Non assigné",
        noDetails: "Aucun détail",
        // FactureDetail
        invoiceDetails: "DÉTAILS DE LA FACTURE",
        invoiceInformation: "Informations de la facture",
        number: "Numéro:",
        numberOfProducts: "Nombre de produits:",
        notes: "Notes:",
        invoiceImage: "Image de la facture:",
        download: "Télécharger",
        downloadInvoiceImage: "Télécharger l'image de la facture",
        purchasedProducts: "Produits achetés",
        unitPrice: "Prix unitaire",
        totalPrice: "Prix total",
        expirationDate: "Date d'expiration",
        lotNumber: "Numéro de lot",
        loadingInvoice: "Chargement de la facture...",
        errorLoadingInvoice: "Impossible de charger la facture.",
        invoiceNotFound: "La facture demandée n'existe pas.",
        noPermissionInvoice: "Vous n'avez pas la permission d'accéder à cette facture.",
        missingInvoiceId: "ID de la facture manquant.",
        errorDownloadingImage: "Erreur lors du téléchargement de l'image de la facture.",
        // StocksPharmacie
        pharmacyStock: "STOCK PHARMACIE",
        expiringSoonAlert: "Expiration proche",
        showAll: "Afficher tout",
        productId: "ID du produit",
        expired: "Expiré",
        days: "jours",
        errorLoadingStocks: "Impossible de charger les stocks.",
        // UtilisationsPharmacie
        productUsages: "UTILISATIONS DES PRODUITS",
        quantityUsed: "Quantité utilisée",
        usageDate: "Date d'utilisation",
        healthRecordId: "ID fiche de santé",
        stockId: "ID stock",
        errorLoadingUsages: "Impossible de charger les utilisations.",
        // Parametres
        pageUnderConstruction: "Page en construction",
        settingsComingSoon: "Les paramètres seront disponibles prochainement.",
        // ProduitsPharmaceutiques
        vaccine: "Vaccin",
        dewormer: "Vermifuge",
        antibiotic: "Antibiotique",
        vitamin: "Vitamine",
        disinfectant: "Désinfectant",
        tablet: "Comprimé",
        bottle: "Flacon",
        unit: "Unité",
        // ModifierUser
        errorLoadingUser: "Impossible de charger l'utilisateur.",
        userNotFound: "L'utilisateur demandé n'existe pas.",
        noPermissionUser: "Vous n'avez pas la permission d'accéder à cet utilisateur.",
        eventList: "LISTE DES ÉVÉNEMENTS",
        addEvent: "Ajouter un événement",
        searchByEvent: "Rechercher par type d'événement...",
        filterByEventType: "Par type d'événement",
        filterByAnimal: "Par animal",
        filterByDate: "Par date",
        birth: "Naissance",
        purchase: "Achat",
        sale: "Vente",
        // Messages communs
        retry: "Réessayer",
        checkConsole: "Vérifiez la console du navigateur (F12) pour plus de détails.",
        // Formulaires communs
        requiredFields: "Champs obligatoires :",
        requiredFieldNote: "Les champs marqués d'un astérisque rouge sont obligatoires.",
        save: "Enregistrer",
        saving: "Enregistrement...",
        cancel: "Annuler",
        confirm: "Confirmer",
        edit: "Modifier",
        delete: "Supprimer",
        deleting: "Suppression...",
        add: "Ajouter",
        select: "Sélectionner",
        selectOption: "-- Sélectionner --",
        selectType: "-- Sélectionner un type --",
        selectStock: "-- Sélectionner un stock --",
        selectHerdFirst: "-- Sélectionnez d'abord un troupeau --",
        // Pages Sante
        addHealthRecord: "Ajouter une fiche de santé",
        massTreatment: "Traitement de masse",
        massTreatmentTitle: "TRAITEMENT DE MASSE",
        chooseHerd: "Choisir le troupeau",
        fullHerd: "Troupeau complet",
        herdSelected: "Le troupeau complet est sélectionné.",
        animalsSelected: "animal(s) sélectionné(s).",
        verifyAnimals: "Vérifier les animaux concernés",
        selectMultipleHint: "Maintenez Ctrl (ou Cmd sur Mac) pour sélectionner plusieurs animaux",
        interventionType: "Type d'intervention",
        interventionDate: "Date d'intervention",
        description: "Description",
        nextIntervention: "Prochaine intervention",
        quantity: "Quantité",
        treatmentSuccess: "Traitement de masse enregistré avec succès !",
        treatmentError: "Une erreur s'est produite lors de l'enregistrement du traitement de masse.",
        errorLoadingHealth: "Impossible de charger les fiches de santé.",
        massUpdateError: "Une erreur s'est produite lors de la mise à jour en masse.",
        prophylaxisUpdatedSuccess: "Prophylaxies mises à jour avec succès !",
        unknown: "Inconnu",
        loadingData: "Chargement des données...",
        editSelectedRecords: "Modifier les fiches sélectionnées",
        selectStatus: "-- Sélectionner un statut --",
        productName: "Nom du produit",
        searchByIntervention: "Rechercher par produit, animal, type d'intervention...",
        filterByInterventionType: "Par type d'intervention",
        noInterventionsFound: "Aucune intervention trouvée avec les filtres sélectionnés.",
        noInterventionsRegistered: "Aucune intervention enregistrée.",
        interventionTypePlaceholder: "Vaccination, Traitement, etc.",
        editSelection: "Modifier la sélection",
        addHealthCard: "AJOUTER UNE FICHE DE SANTÉ",
        editHealthCard: "MODIFIER LA FICHE DE SANTÉ",
        // Pages générales
        animal: "Animal",
        herd: "Troupeau",
        selectAnimal: "-- Sélectionner un animal --",
        selectHerd: "-- Sélectionner un troupeau --",
        details: "Détails",
        pages: {
          "/dashboard": "Dashboard",
          "/animaux": "Animaux",
          "/sante": "Prophylaxie Et Traitement Curatif",
          "/reproductions": "Reproductions",
          "/evenements": "Evenements",
          "/troupeaux": "Troupeaux",
          "/ranches": "Ranch de Provenance",
          "/rapports": "Rapports",
          "/alertes": "Alertes",
          "/dashboard-pharmacie": "Dashboard Pharmacie",
          "/produits-pharmaceutiques": "Produits Pharmaceutiques",
          "/ajouter-produit-pharmaceutique": "Ajouter Produit",
          "/factures": "Factures",
          "/ajouter-facture": "Ajouter Facture",
          "/stocks-pharmacie": "Stocks Pharmacie",
          "/utilisations-pharmacie": "Utilisations",
          "/enregistrer-berger": "Enregistrer Berger",
          "/ajouter-animal": "Ajouter Animal",
          "/ajouter-troupeau": "Ajouter Troupeau",
          "/ajouter-ranch": "Ajouter Ranch",
          "/ajouter-sante": "Ajouter Prophylaxie",
          "/ajouter-reproduction": "Ajouter Reproduction",
          "/ajouter-evenement": "Ajouter Evenement",
          "/ajouter-user": "Ajouter Utilisateur",
          "/traitement-masse": "Traitement de Masse",
          "/parametres": "Paramètres",
        },
        // Clés supplémentaires pour les formulaires
        fieldRequired: "Ce champ est obligatoire",
        yes: "Oui",
        no: "Non",
        registering: "Enregistrement...",
        register: "Enregistrer",
        verifyInfoBeforeConfirm: "Veuillez vérifier les informations avant de confirmer l'enregistrement :",
        verifyInfoBeforeAdd: "Veuillez vérifier les informations avant de confirmer l'ajout :",
        noName: "Sans nom",
        notSelected: "Non sélectionné",
        herdNameLabel: "Nom du troupeau",
        shepherdField: "Berger (terrain)",
        selectShepherd: "Sélectionner un berger",
        ranchNameLabel: "Nom du ranch",
        ranchCode: "Code",
        ranchCodeOptional: "Code du ranch (optionnel)",
        fullAddress: "Adresse complète",
        cityRegion: "Ville, région...",
        contactPerson: "Personne de contact",
        contactPersonName: "Nom de la personne de contact",
        productNameLabel: "Nom du produit",
        productTypeLabel: "Type de produit",
        unitLabel: "Unité",
        selectUnit: "-- Sélectionner une unité --",
        productDescriptionOptional: "Description du produit (optionnel)",
        activeProduct: "Produit actif",
        hireDate: "Date d'embauche",
        address: "Adresse",
        interventionTypeLabel: "Type d'intervention",
        statusLabel: "Statut",
        treated: "Traité",
        notTreated: "Non Traité",
        productsUsed: "Produits utilisés",
        quantityLabel: "Quantité",
        notesOptional: "Notes (optionnel)",
        damLabel: "Génitrice",
        sireLabel: "Géniteur",
        heatDate: "Date de chaleur",
        breedingDate: "Date de saillie",
        calvingDate: "Date de vêlage",
        calvesNumber: "Nombre de veaux",
        eventTypeLabel: "Type d'événement",
        eventDate: "Date de l'événement",
        detailsLabel: "Détails",
        eventDetails: "Détails de l'événement",
        confirmAdd: "Confirmer l'ajout",
        reproductionNotes: "Notes sur la reproduction",
        statusInProgress: "En cours",
        statusCompleted: "Terminé",
        statusFailed: "Échec",
        editShepherd: "MODIFIER LE BERGER",
        addHerdTitle: "AJOUTER UN TROUPEAU",
        addRanchTitle: "AJOUTER UN RANCH DE PROVENANCE",
        addReproductionTitle: "AJOUTER UNE REPRODUCTION",
        addEventTitle: "AJOUTER UN ÉVÉNEMENT",
        addProductTitle: "AJOUTER UN PRODUIT PHARMACEUTIQUE",
        basicInformation: "Informations de base",
        // Clés summary manquantes
        summaryFirstName: "Prénom :",
        summaryLastName: "Nom :",
        summaryHireDate: "Date d'embauche :",
        summaryActive: "Actif :",
        summaryFullName: "Nom complet :",
        summaryAddress: "Adresse :",
        summaryDate: "Date :",
        summaryDam: "Génitrice :",
        summarySire: "Géniteur :",
        summaryHeatDate: "Date de chaleur :",
        summaryBreedingDate: "Date de saillie :",
        summaryCalvingDate: "Date de vêlage :",
        summaryCalvesNumber: "Nombre de veaux :",
        summaryEventType: "Type d'événement :",
        summaryDetails: "Détails :",
        summaryStatus: "Statut :",
        invalidEmail: "Veuillez entrer une adresse email valide (ex: john.doe@example.com)",
        // AjouterAnimal - clés supplémentaires
        selectSex: "-- Sélectionner le sexe --",
        selectBreed: "-- Sélectionner une race --",
        selectCoat: "-- Sélectionner une robe --",
        selectCategory: "-- Sélectionner une catégorie --",
        selectRanch: "-- Sélectionner un ranch --",
        createNewBreed: "+ Créer une nouvelle race",
        createNewCoat: "+ Créer une nouvelle robe",
        createNewCategory: "+ Créer une nouvelle catégorie",
        createNewRanch: "+ Créer un nouveau ranch",
        newBreedName: "Nom de la nouvelle race",
        newCoatName: "Nom de la nouvelle robe",
        newCategoryName: "Nom de la nouvelle catégorie",
        newRanchName: "Nom du nouveau ranch",
        selectOrEnterMatricule: "Sélectionner ou saisir le matricule",
        estimatedValueLabel: "Valeur Estimée",
        estimatedValuePlaceholder: "Valeur estimée",
        // EnregistrerBerger
        registerShepherd: "ENREGISTRER UN BERGER",
        showList: "Afficher la liste des bergers",
        hideList: "Masquer la liste",
        shepherdList: "Liste des bergers",
        noShepherdRegistered: "Aucun berger enregistré.",
        phone: "Téléphone",
        cni: "CNI",
        location: "Localisation",
        locationHint: "(Localisation où se trouve le berger avec son troupeau)",
        actions: "Actions",
        shepherdRegisteredSuccess: "Berger enregistré avec succès !",
        shepherdRegisterError: "Une erreur s'est produite lors de l'enregistrement.",
        loadingListError: "Erreur lors du chargement de la liste.",
        confirmRegistration: "Confirmer l'enregistrement",
        reset: "Réinitialiser",
        // Clés supplémentaires pour Modifier* et AjouterFacture
        chooseSex: "-- Choisir sexe --",
        selectOptional: "-- Sélectionner (optionnel) --",
        selectProduct: "-- Sélectionner un produit --",
        supplier: "Fournisseur",
        supplierName: "Nom du fournisseur",
        invoiceNumber: "Numéro",
        purchaseDate: "Date d'achat",
        totalAmountLabel: "Montant total",
        numberOfProducts: "Nombre de produits",
        unitPrice: "Prix unitaire (FCFA)",
        additionalNotes: "Notes additionnelles (optionnel)",
        lotNumberPlaceholder: "Ex: LOT-001",
        invoiceNumberPlaceholder: "Ex: FAC-2025-001",
        invoiceNumberLabel: "Numéro de facture",
        purchaseDateLabel: "Date d'achat",
        autoCalculated: "Calculé automatiquement",
        selectGroups: "Sélectionner des groupes",
        interventionDescription: "Description de l'intervention",
        // AjouterAnimal - clés principales
        animalOrigin: "Origine de l'animal",
        sexAndCategoryRequired: "Le sexe et la catégorie sont toujours obligatoires.",
        birthRequiredFields: "Pour une naissance, la Date de naissance, le Géniteur et la Génitrice sont également obligatoires.",
        purchaseRequiredFields: "Pour un achat, la Date d'entrée est également obligatoire.",
        birthDate: "Date de naissance",
        entryDate: "Date d'entrée",
        matricule: "Matricule",
        sex: "Sexe",
        category: "Catégorie",
        herd: "Troupeau",
        sire: "Géniteur",
        dam: "Génitrice",
        breed: "Race",
        coat: "Robe",
        value: "Valeur",
        originRanch: "Ranch de provenance",
        // ModifierAnimal - clés de suppression
        deleteAnimal: "Supprimer",
        animalDeletedSuccess: "Animal supprimé avec succès !",
        animalDeleteError: "Une erreur s'est produite lors de la suppression de l'animal.",
        invalidMatricule: "Le matricule saisi ne correspond pas. Veuillez réessayer.",
        confirmDelete: "Confirmer la suppression",
        confirmDeleteTitle: "CONFIRMER LA SUPPRESSION",
        deleteWarning: "⚠️ Attention : Cette action est irréversible",
        deleteWarningText: "La suppression de cet animal entraînera :",
        deleteWarning1: "Perte permanente de toutes les données de l'animal",
        deleteWarning2: "Suppression de tous les événements associés",
        deleteWarning3: "Suppression de tous les dossiers de santé",
        deleteWarning4: "Suppression de toutes les reproductions liées",
        deleteWarning5: "Impact sur les relations généalogiques (géniteur/génitrice)",
        enterMatricule: "Pour confirmer, veuillez saisir le matricule de l'animal :",
        enterMatriculePlaceholder: "Saisir le matricule",
      },
      en: {
        appName: "Ngaoundaba Ranch",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        disconnect: "Logout",
        language: "Language",
        settings: "Settings",
        createUser: "Create User",
        darkMode: "Dark Mode",
        lightMode: "Light Mode",
        loading: "Loading...",
        guest: "Guest",
        user: "User",
        // Navigation sidebar
        navDashboard: "Dashboard",
        navAnimals: "Animals",
        navHerds: "Herds",
        navProphylaxis: "Prophylaxis And Curative Treatment",
        navPharmacy: "Pharmacy",
        navEvents: "Events",
        navReports: "Reports",
        navAlerts: "Alerts",
        navUsers: "Users",
        navShepherds: "Shepherds",
        navDisconnect: "Disconnect",
        brandSubtitle: "Livestock Management",
        // Error messages
        errorLoadingDashboard: "Unable to load dashboard data.",
        sessionExpired: "Session expired. Redirecting...",
        accessDenied: "Access denied. You do not have the necessary permissions.",
        endpointNotFound: "API endpoint not found. Check the configuration.",
        serverError: "Server error. Please try again later.",
        connectionError: "Unable to connect to server. Check your connection and API URL.",
        configError: "Configuration error",
        // Dashboard
        welcomeDashboard: "WELCOME TO THE DASHBOARD",
        error: "ERROR",
        loadingDashboard: "Loading dashboard...",
        statistics: "Herd Statistics",
        indicators: "Indicators (%)",
        monthlyEvolution: "Monthly Evolution",
        sexDistribution: "Distribution By Sex",
        categoryDistribution: "Distribution By Category",
        animals: "animals",
        numberOfAnimals: "Number of animals",
        // Common labels
        total: "Total",
        males: "Males",
        females: "Females",
        // StatCard Dashboard labels
        initialStock: "INITIAL STOCK",
        births: "BIRTHS",
        mortality: "MORTALITY",
        purchases: "PURCHASES",
        sales: "SALES",
        zakat: "ZAKAT",
        finalStock: "FINAL STOCK",
        estimatedValue: "ESTIMATED VALUE",
        // StatCard FicheAnimal labels
        estimatedValueFull: "ESTIMATED VALUE",
        prophylaxes: "PROPHYLAXES",
        reproductions: "REPRODUCTIONS",
        events: "EVENTS",
        age: "AGE",
        status: "STATUS",
        notProvided: "Not provided",
        active: "Active",
        // StatCard DashboardPharmacie labels
        activeProducts: "Active products",
        availableStocks: "Available stocks",
        lowStock: "Low stock",
        expiringSoon: "Expiring soon",
        totalInvoices: "Total invoices",
        totalAmount: "Total amount",
        overview: "Overview",
        quickActions: "Quick Actions",
        see: "See",
        category: "Category",
        pharmacyDashboard: "PHARMACY DASHBOARD",
        addProduct: "Add product",
        addInvoice: "Add invoice",
        viewUsages: "View usages",
        // List pages
        animalList: "ANIMAL LIST",
        addAnimal: "Add animal",
        filters: "Filters",
        filterBySex: "By sex",
        filterByStatus: "By status",
        filterByCategory: "By category",
        filterByHerd: "By herd",
        male: "Male",
        female: "Female",
        sold: "Sold",
        dead: "Dead",
        cannotEditSoldOrDead: "Sold or dead animals cannot be modified",
        errorLoadingAnimals: "Unable to load animal data.",
        noAnimalsFound: "No animals found with the selected filters.",
        noAnimalsRegistered: "No animals registered.",
        notAvailable: "N/A",
        herdList: "HERD LIST",
        addHerd: "Add herd",
        searchByMatricule: "Search by matricule, coat, breed...",
        searchByHerdName: "Search by herd name...",
        filterByUser: "By user",
        filterByShepherd: "By shepherd",
        noHerdRegistered: "No herd registered.",
        // Troupeaux
        herdName: "Herd Name",
        responsibleUser: "Responsible (User)",
        shepherd: "Shepherd",
        viewMore: "View more",
        loadingHerds: "Loading herds...",
        errorLoadingHerds: "Unable to load herd data.",
        // Users
        firstName: "First name",
        lastName: "Last name",
        groups: "Groups",
        loadingUsers: "Loading users...",
        errorLoadingUsers: "Unable to load users.",
        errorDeletingUser: "An error occurred while deleting.",
        confirmDeleteUser: "Are you sure you want to delete this user?",
        userDeletedSuccess: "User deleted successfully!",
        // Login
        welcome: "Welcome",
        heroSubtitle: "Manage your livestock, alerts and reports from a single modern interface.",
        dashboardRealtime: "Real-time Dashboard",
        alertTracking: "Alert Tracking",
        detailedReports: "Detailed Reports",
        loginTitle: "Login",
        loginSubtitle: "Access your management space",
        loginButton: "Log in",
        loggingIn: "Logging in...",
        loginError: "Incorrect username or password.",
        loginHelp: "Need help? Contact your administrator to reset your access.",
        // Rapports
        herdReport: "HERD REPORT",
        keyIndicators: "Key Indicators",
        element: "Element",
        value: "Value",
        totalStock: "Total Stock",
        birthStatistics: "Birth Statistics",
        indicator: "Indicator",
        numberOfCalves: "Number of calves",
        numberOfHeifers: "Number of heifers",
        numberOfCows: "Number of cows",
        birthRatePerCows: "Birth rate / cows (%)",
        mortalityStatistics: "Mortality Statistics",
        deaths: "Deaths",
        mortalityRate: "Mortality rate (%)",
        global: "Global",
        herdDistribution: "Herd Distribution",
        stockCount: "Stock Count",
        herdValue: "Herd Value",
        loadingReport: "Loading report...",
        errorLoadingReport: "Unable to load global report.",
        // Alertes
        alertsTitle: "ALERTS: UPCOMING INTERVENTIONS",
        viewAnimals: "View animals",
        loadingAlerts: "Loading alerts...",
        errorLoadingAlerts: "Error loading alerts.",
        noInterventionsScheduled: "No interventions scheduled in the next 7 days.",
        animalDetails: "Animal details",
        product: "Product",
        close: "Close",
        // RapportTroupeau
        herdReportTitle: "HERD REPORT",
        reportTitle: "REPORT:",
        shepherdName: "Shepherd name",
        calculatedTotal: "Calculated total",
        currentStock: "Current stock",
        numberOfAnimalsByCategory: "Number of",
        noReportAvailable: "No report available for this herd.",
        errorLoadingHerdReport: "Unable to load herd report.",
        herdNotFound: "The requested herd does not exist or the report is not available.",
        noPermissionReport: "You do not have permission to access this report.",
        missingHerdId: "Missing herd ID.",
        back: "Back",
        // Historique
        historyTitle: "History",
        searchHistory: "Search in history...",
        action: "Action",
        loadingHistory: "Loading history...",
        errorLoadingHistory: "Error loading history.",
        noHistoryRecorded: "No history recorded yet.",
        filterByAction: "Filter by action",
        filterByAnimal: "Filter by animal",
        startDate: "Start date",
        endDate: "End date",
        care: "Care",
        feeding: "Feeding",
        notAssigned: "Not assigned",
        noDetails: "No details",
        // FactureDetail
        invoiceDetails: "INVOICE DETAILS",
        invoiceInformation: "Invoice information",
        number: "Number:",
        numberOfProducts: "Number of products:",
        notes: "Notes:",
        invoiceImage: "Invoice image:",
        download: "Download",
        downloadInvoiceImage: "Download invoice image",
        purchasedProducts: "Purchased products",
        unitPrice: "Unit price",
        totalPrice: "Total price",
        expirationDate: "Expiration date",
        lotNumber: "Lot number",
        loadingInvoice: "Loading invoice...",
        errorLoadingInvoice: "Unable to load invoice.",
        invoiceNotFound: "The requested invoice does not exist.",
        noPermissionInvoice: "You do not have permission to access this invoice.",
        missingInvoiceId: "Missing invoice ID.",
        errorDownloadingImage: "Error downloading invoice image.",
        // StocksPharmacie
        pharmacyStock: "PHARMACY STOCK",
        expiringSoonAlert: "Expiring soon",
        showAll: "Show all",
        productId: "Product ID",
        expired: "Expired",
        days: "days",
        errorLoadingStocks: "Unable to load stocks.",
        // UtilisationsPharmacie
        productUsages: "PRODUCT USAGES",
        quantityUsed: "Quantity used",
        usageDate: "Usage date",
        healthRecordId: "Health record ID",
        stockId: "Stock ID",
        errorLoadingUsages: "Unable to load usages.",
        // Parametres
        pageUnderConstruction: "Page under construction",
        settingsComingSoon: "Settings will be available soon.",
        // ProduitsPharmaceutiques
        vaccine: "Vaccine",
        dewormer: "Dewormer",
        antibiotic: "Antibiotic",
        vitamin: "Vitamin",
        disinfectant: "Disinfectant",
        tablet: "Tablet",
        bottle: "Bottle",
        unit: "Unit",
        // ModifierUser
        errorLoadingUser: "Unable to load user.",
        userNotFound: "The requested user does not exist.",
        noPermissionUser: "You do not have permission to access this user.",
        eventList: "EVENT LIST",
        addEvent: "Add event",
        searchByEvent: "Search by event type...",
        filterByEventType: "By event type",
        filterByAnimal: "By animal",
        filterByDate: "By date",
        birth: "Birth",
        purchase: "Purchase",
        sale: "Sale",
        startDate: "Start date",
        endDate: "End date",
        // AjouterUser
        addUser: "ADD USER",
        username: "Username",
        password: "Password",
        email: "Email",
        name: "Name",
        emailPlaceholder: "Ex: john.doe@example.com",
        invalidEmail: "Please enter a valid email address (ex: john.doe@example.com)",
        userAddedSuccess: "User added successfully!",
        userAddError: "An error occurred while registering the user.",
        healthDetail: "HEALTH RECORD DETAIL",
        backToList: "Back to list",
        massTreatmentTitle: "MASS TREATMENT",
        chooseHerd: "Choose herd",
        fullHerd: "Full herd",
        herdSelected: "The full herd is selected.",
        animalsSelected: "animal(s) selected.",
        verifyAnimals: "Verify concerned animals",
        selectMultipleHint: "Hold Ctrl (or Cmd on Mac) to select multiple animals",
        interventionType: "Intervention type",
        interventionDate: "Intervention date",
        status: "Status",
        description: "Description",
        nextIntervention: "Next intervention",
        errorLoadingHealth: "Unable to load health records.",
        massUpdateError: "An error occurred during mass update.",
        prophylaxisUpdatedSuccess: "Prophylaxis updated successfully!",
        unknown: "Unknown",
        loadingData: "Loading data...",
        editSelectedRecords: "Edit selected records",
        selectStatus: "-- Select a status --",
        productName: "Product name",
        searchByIntervention: "Search by product, animal, intervention type...",
        filterByInterventionType: "By intervention type",
        noInterventionsFound: "No interventions found with the selected filters.",
        noInterventionsRegistered: "No interventions registered.",
        interventionTypePlaceholder: "Vaccination, Treatment, etc.",
        productsUsed: "Products used",
        addProduct: "Add product",
        removeProduct: "Remove",
        quantity: "Quantity",
        treatmentSuccess: "Mass treatment registered successfully!",
        treatmentError: "An error occurred while registering the mass treatment.",
        vaccination: "Vaccination",
        treatment: "Treatment",
        deworming: "Deworming",
        other: "Other",
        treated: "Treated",
        notTreated: "Not Treated",
        date: "Date",
        // EnregistrerBerger
        registerShepherd: "REGISTER SHEPHERD",
        showList: "Show shepherd list",
        hideList: "Hide list",
        shepherdList: "Shepherd List",
        noShepherdRegistered: "No shepherd registered.",
        firstName: "First name",
        lastName: "Last name",
        phone: "Phone",
        cni: "ID Card",
        address: "Address",
        actions: "Actions",
        shepherdRegisteredSuccess: "Shepherd registered successfully!",
        shepherdRegisterError: "An error occurred while registering.",
        loadingListError: "Error loading list.",
        location: "Location",
        locationHint: "(Location where the shepherd is with his herd)",
        reset: "Reset",
        confirmRegistration: "Confirm registration",
        // Additional keys for Modifier* and AjouterFacture
        chooseSex: "-- Choose sex --",
        selectOptional: "-- Select (optional) --",
        selectProduct: "-- Select a product --",
        supplier: "Supplier",
        supplierName: "Supplier name",
        invoiceNumber: "Number",
        purchaseDate: "Purchase date",
        totalAmountLabel: "Total amount",
        numberOfProducts: "Number of products",
        unitPrice: "Unit price (FCFA)",
        additionalNotes: "Additional notes (optional)",
        lotNumberPlaceholder: "Ex: LOT-001",
        invoiceNumberPlaceholder: "Ex: FAC-2025-001",
        invoiceNumberLabel: "Invoice number",
        purchaseDateLabel: "Purchase date",
        autoCalculated: "Auto calculated",
        selectGroups: "Select groups",
        interventionDescription: "Intervention description",
        summaryUsername: "Username:",
        summaryFirstName: "First name:",
        summaryLastName: "Last name:",
        summaryEmail: "Email:",
        summaryPhone: "Phone:",
        summaryCNI: "ID Card:",
        summaryLocation: "Location:",
        // AjouterAnimal
        addAnimal: "ADD ANIMAL",
        sexAndCategoryRequired: "Sex and Category are always required.",
        birthRequiredFields: "For a birth, Birth Date, Sire and Dam are also required.",
        purchaseRequiredFields: "For a purchase, Entry Date is also required.",
        birthDate: "Birth Date",
        entryDate: "Entry Date",
        matricule: "Matricule",
        sex: "Sex",
        category: "Category",
        herd: "Herd",
        sire: "Sire",
        dam: "Dam",
        breed: "Breed",
        coat: "Coat",
        value: "Value",
        originRanch: "Origin Ranch",
        summaryMatricule: "Matricule:",
        summarySex: "Sex:",
        summaryBirthDate: "Birth date:",
        summaryEntryDate: "Entry date:",
        summaryBreed: "Breed:",
        summaryCoat: "Coat:",
        summaryCategory: "Category:",
        summaryStatus: "Status:",
        summaryValue: "Value:",
        summaryHerd: "Herd:",
        summarySire: "Sire:",
        summaryDam: "Dam:",
        summaryOriginRanch: "Origin ranch:",
        confirmAdd: "Confirm addition",
        animalAddedSuccess: "Animal added successfully!",
        animalAddError: "An error occurred while adding the animal.",
        animalOrigin: "Animal origin",
        verifyInfo: "Please verify the animal information before confirming the addition:",
        modifyAnimal: "MODIFY ANIMAL",
        animalModifiedSuccess: "Animal modified successfully!",
        animalModifyError: "An error occurred while modifying the animal.",
        deleteAnimal: "Delete animal",
        confirmDeleteAnimal: "Are you sure you want to delete this animal?",
        animalDeletedSuccess: "Animal deleted successfully!",
        animalDeleteError: "An error occurred while deleting the animal.",
        invalidMatricule: "The entered matricule does not match. Please try again.",
        confirmDelete: "Confirm deletion",
        deleteWarning: "⚠️ Warning: This action is irreversible",
        deleteWarningText: "Deleting this animal will result in:",
        deleteWarning1: "Permanent loss of all animal data",
        deleteWarning2: "Deletion of all associated events",
        deleteWarning3: "Deletion of all health records",
        deleteWarning4: "Deletion of all related reproductions",
        deleteWarning5: "Impact on genealogical relationships (sire/dam)",
        enterMatricule: "To confirm, please enter the animal's matricule:",
        enterMatriculePlaceholder: "Enter matricule",
        // Factures
        invoiceManagement: "INVOICE MANAGEMENT",
        addInvoice: "Add invoice",
        searchByInvoice: "Search by number, supplier, notes...",
        filterBySupplier: "By supplier",
        filterByDate: "By purchase date",
        supplier: "Supplier",
        purchaseDate: "Purchase date",
        // ProduitsPharmaceutiques
        pharmaceuticalProducts: "PHARMACEUTICAL PRODUCTS",
        addProduct: "Add product",
        searchByProduct: "Search by name, type, manufacturer...",
        filterByType: "By type",
        productType: "Type",
        manufacturer: "Manufacturer",
        // Ranches
        ranchList: "RANCH LIST",
        addRanch: "Add ranch",
        searchByRanch: "Search by ranch name...",
        // Reproductions
        reproductionList: "REPRODUCTION LIST",
        addReproduction: "Add reproduction",
        searchByReproduction: "Search by matricule...",
        filterByStatus: "By status",
        filterByDam: "By dam",
        filterBySire: "By sire",
        productCatalog: "PHARMACEUTICAL PRODUCT CATALOG",
        inactive: "Inactive",
        // Ranches
        ranchList: "PROVENANCE RANCH LIST",
        loadingRanches: "Loading ranches...",
        // Reproductions
        reproductionManagement: "REPRODUCTION MANAGEMENT",
        addReproduction: "Add Reproduction",
        searchByReproduction: "Search by dam, sire, status...",
        inProgress: "In progress",
        completed: "Completed",
        failed: "Failed",
        // Common messages
        retry: "Retry",
        checkConsole: "Check the browser console (F12) for more details.",
        // Common forms
        requiredFields: "Required fields:",
        requiredFieldNote: "Fields marked with a red asterisk are required.",
        save: "Save",
        saving: "Saving...",
        cancel: "Cancel",
        confirm: "Confirm",
        edit: "Edit",
        delete: "Delete",
        deleting: "Deleting...",
        add: "Add",
        select: "Select",
        selectOption: "-- Select --",
        selectType: "-- Select a type --",
        selectStock: "-- Select a stock --",
        selectHerdFirst: "-- Select a herd first --",
        // Sante pages
        addHealthRecord: "Add health record",
        massTreatment: "Mass treatment",
        editSelection: "Edit selection",
        addHealthCard: "ADD HEALTH RECORD",
        editHealthCard: "EDIT HEALTH RECORD",
        // General pages
        animal: "Animal",
        herd: "Herd",
        selectAnimal: "-- Select an animal --",
        selectHerd: "-- Select a herd --",
        details: "Details",
        pages: {
          "/dashboard": "Dashboard",
          "/animaux": "Animals",
          "/sante": "Prophylaxis And Curative Treatment",
          "/reproductions": "Reproductions",
          "/evenements": "Events",
          "/troupeaux": "Herds",
          "/ranches": "Ranch de Provenance",
          "/rapports": "Reports",
          "/alertes": "Alerts",
          "/dashboard-pharmacie": "Pharmacy Dashboard",
          "/produits-pharmaceutiques": "Pharmaceutical Products",
          "/ajouter-produit-pharmaceutique": "Add Product",
          "/factures": "Invoices",
          "/ajouter-facture": "Add Invoice",
          "/stocks-pharmacie": "Pharmacy Stock",
          "/utilisations-pharmacie": "Usage",
          "/enregistrer-berger": "Register Shepherd",
          "/ajouter-animal": "Add Animal",
          "/ajouter-troupeau": "Add Herd",
          "/ajouter-sante": "Add Prophylaxis",
          "/ajouter-reproduction": "Add Reproduction",
          "/ajouter-evenement": "Add Event",
          "/ajouter-user": "Add User",
          "/traitement-masse": "Mass Treatment",
          "/parametres": "Settings",
        },
        // Additional keys for forms
        fieldRequired: "This field is required",
        yes: "Yes",
        no: "No",
        registering: "Registering...",
        register: "Register",
        verifyInfoBeforeConfirm: "Please verify the information before confirming registration:",
        verifyInfoBeforeAdd: "Please verify the information before confirming addition:",
        noName: "No name",
        notSelected: "Not selected",
        herdNameLabel: "Herd name",
        shepherdField: "Shepherd (field)",
        selectShepherd: "Select a shepherd",
        ranchNameLabel: "Ranch name",
        ranchCode: "Code",
        ranchCodeOptional: "Ranch code (optional)",
        fullAddress: "Full address",
        cityRegion: "City, region...",
        contactPerson: "Contact person",
        contactPersonName: "Contact person name",
        productNameLabel: "Product name",
        productTypeLabel: "Product type",
        unitLabel: "Unit",
        selectUnit: "-- Select a unit --",
        productDescriptionOptional: "Product description (optional)",
        activeProduct: "Active product",
        hireDate: "Hire date",
        address: "Address",
        interventionTypeLabel: "Intervention type",
        statusLabel: "Status",
        treated: "Treated",
        notTreated: "Not Treated",
        productsUsed: "Products used",
        quantityLabel: "Quantity",
        notesOptional: "Notes (optional)",
        damLabel: "Dam",
        sireLabel: "Sire",
        heatDate: "Heat date",
        breedingDate: "Breeding date",
        calvingDate: "Calving date",
        calvesNumber: "Number of calves",
        eventTypeLabel: "Event type",
        eventDate: "Event date",
        detailsLabel: "Details",
        eventDetails: "Event details",
        confirmAdd: "Confirm addition",
        reproductionNotes: "Notes on reproduction",
        statusInProgress: "In progress",
        statusCompleted: "Completed",
        statusFailed: "Failed",
        editShepherd: "EDIT SHEPHERD",
        addHerdTitle: "ADD HERD",
        addRanchTitle: "ADD ORIGIN RANCH",
        addReproductionTitle: "ADD REPRODUCTION",
        addEventTitle: "ADD EVENT",
        addProductTitle: "ADD PHARMACEUTICAL PRODUCT",
        basicInformation: "Basic information",
        // Missing summary keys
        summaryFirstName: "First name:",
        summaryLastName: "Last name:",
        summaryHireDate: "Hire date:",
        summaryActive: "Active:",
        summaryFullName: "Full name:",
        summaryAddress: "Address:",
        summaryDate: "Date:",
        summaryDam: "Dam:",
        summarySire: "Sire:",
        summaryHeatDate: "Heat date:",
        summaryBreedingDate: "Breeding date:",
        summaryCalvingDate: "Calving date:",
        summaryCalvesNumber: "Number of calves:",
        summaryEventType: "Event type:",
        summaryDetails: "Details:",
        summaryStatus: "Status:",
        // AjouterAnimal - additional keys
        selectSex: "-- Select sex --",
        selectBreed: "-- Select a breed --",
        selectCoat: "-- Select a coat --",
        selectCategory: "-- Select a category --",
        selectRanch: "-- Select a ranch --",
        createNewBreed: "+ Create a new breed",
        createNewCoat: "+ Create a new coat",
        createNewCategory: "+ Create a new category",
        createNewRanch: "+ Create a new ranch",
        newBreedName: "New breed name",
        newCoatName: "New coat name",
        newCategoryName: "New category name",
        newRanchName: "New ranch name",
        selectOrEnterMatricule: "Select or enter matricule",
        estimatedValueLabel: "Estimated Value",
        estimatedValuePlaceholder: "Estimated value",
      },
    };
    return dict[lang] || dict.fr;
  }, [lang]);

  const value = {
    lang,
    setLang,
    t: translations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

// Hook de traduction pour faciliter l'accès
export const useTranslation = () => {
  const { t, lang, setLang } = useLanguage();
  return { t, lang, setLang };
};

